import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { getApiBase, getAuthHeaders } from '../../utils/api';
import {
  Clock,
  Play,
  Pause,
  RefreshCw,
  CheckCircle,
  XCircle,
  AlertCircle,
  Calendar,
  DollarSign,
  ArrowRight,
  Loader2,
  History,
  Settings,
  Eye,
  Zap,
  X,
  CreditCard,
  FileText,
} from 'lucide-react';

// Shared token class strings (mirror the mockup's .btn / .btn-p / .panel / .pill / .lbl).
const BTN = 'inline-flex h-9 items-center gap-1.5 whitespace-nowrap rounded-lg border border-ui-control bg-ui-surface px-3 text-[13px] text-ui-ink-2 hover:bg-ui-hover disabled:cursor-not-allowed disabled:opacity-50';
const BTN_P = 'inline-flex h-10 items-center gap-1.5 whitespace-nowrap rounded-lg bg-ui-primary px-4 text-sm font-semibold text-ui-on-primary hover:bg-ui-primary-hover disabled:cursor-not-allowed disabled:opacity-50';
const BTN_DANGER = 'inline-flex h-8 items-center gap-1.5 whitespace-nowrap rounded-lg bg-ui-red-solid px-3 text-[13px] font-semibold text-ui-on-primary hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50';
const PANEL = 'rounded-2xl border border-ui-line bg-ui-surface shadow-ui-card';
const LBL = 'text-[11px] font-semibold uppercase tracking-[0.08em] text-ui-muted';
const PILL = 'inline-flex h-6 items-center gap-1.5 whitespace-nowrap rounded-full px-2.5 text-xs font-semibold';
const DOT = <span className="h-1.5 w-1.5 rounded-full bg-current" aria-hidden="true" />;
const INPUT = 'h-9 rounded-lg border border-ui-control bg-ui-surface px-2.5 text-[13px] text-ui-ink';
const SUB_ROW = 'flex items-center justify-between gap-3 rounded-lg border border-ui-divider bg-ui-sunk px-3 py-2.5 text-sm';
const TH = 'whitespace-nowrap border-b border-ui-divider bg-ui-sunk px-4 py-2.5 text-left text-xs font-semibold text-ui-muted';
const TD = 'border-b border-ui-divider px-4 py-3 text-ui-ink-2';

const TONE = {
  green: 'bg-ui-green-bg text-ui-green-fg',
  amber: 'bg-ui-amber-bg text-ui-amber-fg',
  red: 'bg-ui-red-bg text-ui-red-fg',
  blue: 'bg-ui-blue-bg text-ui-blue-fg',
  neutral: 'bg-ui-hover text-ui-ink-3',
};

const Notice = ({ tone, icon, children, onDismiss, dismissLabel }) => (
  <div className={`flex items-center gap-2 rounded-xl border p-3 text-sm ${
    tone === 'red' ? 'border-ui-red-line bg-ui-red-bg text-ui-red-fg'
      : tone === 'green' ? 'border-ui-green-line bg-ui-green-bg text-ui-green-fg'
      : tone === 'amber' ? 'border-ui-amber-line bg-ui-amber-bg text-ui-amber-fg'
      : 'border-ui-blue-line bg-ui-blue-bg text-ui-blue-fg'
  }`}>
    {icon}
    <span className="min-w-0 flex-1">{children}</span>
    {onDismiss && (
      <button type="button" onClick={onDismiss} aria-label={dismissLabel} className="rounded p-1 hover:opacity-70">
        <X className="h-4 w-4" />
      </button>
    )}
  </div>
);

// Result pill for a scheduler run: green Completed, amber Partial, neutral nothing-to-process, red Failed.
const RunResultPill = ({ run }) => {
  if (!run) return <span className={`${PILL} ${TONE.neutral}`}>{DOT}Never run</span>;
  switch (run.status) {
    case 'completed':
      return run.processed === 0
        ? <span className={`${PILL} ${TONE.neutral}`}>{DOT}Nothing to process</span>
        : <span className={`${PILL} ${TONE.green}`}>{DOT}Completed</span>;
    case 'partial':
      return <span className={`${PILL} ${TONE.amber}`}>{DOT}Partial</span>;
    case 'failed':
      return <span className={`${PILL} ${TONE.red}`}>{DOT}Failed</span>;
    case 'running':
      return <span className={`${PILL} ${TONE.blue}`}>{DOT}Running</span>;
    default:
      return <span className={`${PILL} ${TONE.neutral}`}>{DOT}{run.status || 'Unknown'}</span>;
  }
};

const RUN_INTERVAL_LABELS = {
  daily: 'Daily',
  hourly: 'Hourly',
  '15min': 'Every 15 minutes',
};

/**
 * SchedulerDashboard - Admin component for managing the billing scheduler
 *
 * Displays:
 * - Scheduler status (running, paused, next run)
 * - Quick actions (run all, preview, view history)
 * - Task status for each task type
 * - Pending items preview
 */
const SchedulerDashboard = ({ currentUser }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [status, setStatus] = useState(null);
  const [preview, setPreview] = useState(null);
  const [showConfig, setShowConfig] = useState(false);

  // Date range search state
  const [showDateSearch, setShowDateSearch] = useState(false);
  const [dateSearchStart, setDateSearchStart] = useState('');
  const [dateSearchEnd, setDateSearchEnd] = useState('');
  const [scheduledCharges, setScheduledCharges] = useState(null);
  const [dateSearchLoading, setDateSearchLoading] = useState(false);

  // Date simulation state
  const [showSimulation, setShowSimulation] = useState(false);
  const [simulationDate, setSimulationDate] = useState('');
  const [simulationPreview, setSimulationPreview] = useState(null);
  const [simulationLoading, setSimulationLoading] = useState(false);

  // Test execution state
  const [stripeSandboxMode, setStripeSandboxMode] = useState(null);
  const [testExecutionResults, setTestExecutionResults] = useState(null);
  const [showTestConfirm, setShowTestConfirm] = useState(false);

  const isSuperAdmin = currentUser?.role === 'superadmin';
  const hasPermission = isSuperAdmin || currentUser?.admin_permissions?.includes('billing_scheduler');

  const fetchStatus = useCallback(async () => {
    try {
      const response = await axios.get(
        `${getApiBase()}/admin/scheduler/status`,
        { headers: getAuthHeaders() }
      );
      setStatus(response.data);
    } catch (err) {
      console.error('Error fetching scheduler status:', err);
      if (err.response?.status === 503) {
        setError('Scheduler service not available. Check server configuration.');
      } else if (err.response?.status !== 403) {
        setError('Failed to fetch scheduler status');
      }
    }
  }, []);

  const fetchPreview = useCallback(async () => {
    try {
      const response = await axios.get(
        `${getApiBase()}/admin/scheduler/preview`,
        { headers: getAuthHeaders() }
      );
      setPreview(response.data);
    } catch (err) {
      console.error('Error fetching preview:', err);
    }
  }, []);

  const fetchStripeMode = useCallback(async () => {
    try {
      const response = await axios.get(
        `${getApiBase()}/admin/scheduler/stripe-mode`,
        { headers: getAuthHeaders() }
      );
      setStripeSandboxMode(response.data);
    } catch (err) {
      console.error('Error fetching Stripe mode:', err);
    }
  }, []);

  useEffect(() => {
    if (hasPermission) {
      setLoading(true);
      Promise.all([fetchStatus(), fetchPreview(), fetchStripeMode()])
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, [hasPermission, fetchStatus, fetchPreview, fetchStripeMode]);

  // Auto-refresh every 30 seconds
  useEffect(() => {
    if (!hasPermission) return;

    const interval = setInterval(() => {
      fetchStatus();
      fetchPreview();
    }, 30000);

    return () => clearInterval(interval);
  }, [hasPermission, fetchStatus, fetchPreview]);

  const handlePauseResume = async () => {
    setError('');
    setSuccess('');
    setActionLoading(true);

    try {
      const endpoint = status?.config?.paused ? 'resume' : 'pause';
      await axios.post(
        `${getApiBase()}/admin/scheduler/${endpoint}`,
        {},
        { headers: getAuthHeaders() }
      );
      setSuccess(status?.config?.paused ? 'Scheduler resumed' : 'Scheduler paused');
      await fetchStatus();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to update scheduler');
    } finally {
      setActionLoading(false);
    }
  };

  const handleTrigger = async (taskType = 'all', dryRun = false) => {
    setError('');
    setSuccess('');
    setActionLoading(true);

    try {
      const response = await axios.post(
        `${getApiBase()}/admin/scheduler/trigger`,
        { task_type: taskType, dry_run: dryRun },
        { headers: getAuthHeaders() }
      );
      setSuccess(response.data.message);
      await Promise.all([fetchStatus(), fetchPreview()]);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to trigger scheduler');
    } finally {
      setActionLoading(false);
    }
  };

  const handleTriggerSubscription = async (subscriptionId, dryRun = false) => {
    setError('');
    setSuccess('');
    setActionLoading(true);

    try {
      const response = await axios.post(
        `${getApiBase()}/admin/scheduler/trigger/${subscriptionId}`,
        { dry_run: dryRun, override_due_check: false },
        { headers: getAuthHeaders() }
      );
      setSuccess(response.data.message);
      await Promise.all([fetchStatus(), fetchPreview()]);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to trigger billing');
    } finally {
      setActionLoading(false);
    }
  };

  // Search for scheduled charges in date range
  const handleDateSearch = async () => {
    if (!dateSearchStart || !dateSearchEnd) {
      setError('Please select both start and end dates');
      return;
    }

    setError('');
    setDateSearchLoading(true);

    try {
      const response = await axios.get(
        `${getApiBase()}/admin/scheduler/scheduled-charges`,
        {
          params: { start_date: dateSearchStart, end_date: dateSearchEnd },
          headers: getAuthHeaders()
        }
      );
      setScheduledCharges(response.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to fetch scheduled charges');
    } finally {
      setDateSearchLoading(false);
    }
  };

  // Get preview for a specific date
  const handleGetPreviewForDate = async () => {
    if (!simulationDate) {
      setError('Please select a date');
      return;
    }

    setError('');
    setSimulationLoading(true);

    try {
      const response = await axios.get(
        `${getApiBase()}/admin/scheduler/preview-for-date`,
        {
          params: { as_of_date: simulationDate },
          headers: getAuthHeaders()
        }
      );
      setSimulationPreview(response.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to fetch preview');
    } finally {
      setSimulationLoading(false);
    }
  };

  // Run simulation for a specific date
  const handleRunSimulation = async () => {
    if (!simulationDate) {
      setError('Please select a date');
      return;
    }

    setError('');
    setSuccess('');
    setSimulationLoading(true);

    try {
      const response = await axios.post(
        `${getApiBase()}/admin/scheduler/simulate`,
        {},
        {
          params: { as_of_date: simulationDate },
          headers: getAuthHeaders()
        }
      );
      setSuccess(response.data.message);
      // Refresh the preview to show updated state
      await handleGetPreviewForDate();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to run simulation');
    } finally {
      setSimulationLoading(false);
    }
  };

  // Execute test billing (actual charges in sandbox mode)
  const handleExecuteTest = async () => {
    if (!simulationDate) {
      setError('Please select a date');
      return;
    }

    if (!stripeSandboxMode?.sandbox_mode) {
      setError('Test execution is only available in Stripe sandbox mode');
      return;
    }

    setError('');
    setSuccess('');
    setSimulationLoading(true);
    setShowTestConfirm(false);
    setTestExecutionResults(null);

    try {
      const response = await axios.post(
        `${getApiBase()}/admin/scheduler/execute-test`,
        {},
        {
          params: { as_of_date: simulationDate, confirm_sandbox: true },
          headers: getAuthHeaders()
        }
      );
      setTestExecutionResults(response.data);
      if (response.data.success) {
        setSuccess(response.data.message);
      } else {
        setError(response.data.message);
      }
      // Refresh data after execution
      await Promise.all([fetchStatus(), fetchPreview(), handleGetPreviewForDate()]);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to execute test billing');
    } finally {
      setSimulationLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'Never';
    const date = new Date(dateStr);
    return date.toLocaleString();
  };

  const formatAmount = (cents) => {
    if (!cents) return '$0.00';
    return `$${(cents / 100).toFixed(2)}`;
  };

  if (!hasPermission) {
    return (
      <div className="p-6">
        <Notice tone="amber" icon={<AlertCircle className="h-5 w-5 shrink-0" />}>
          You don't have permission to access the billing scheduler.
        </Notice>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center p-6 text-ui-ink-2">
        <Loader2 className="h-6 w-6 animate-spin text-ui-link" />
        <span className="ml-2">Loading scheduler status...</span>
      </div>
    );
  }

  // Derived, display-only values from the status/preview the page already loads.
  const config = status?.config;
  const statusLabel = !config?.enabled
    ? 'Disabled'
    : config?.paused
    ? 'Paused'
    : status?.is_running
    ? 'Running'
    : 'Active';
  const statusTone = !config?.enabled ? TONE.neutral : config?.paused ? TONE.amber : TONE.green;

  const taskRuns = [
    { key: 'billing', label: 'Billing charges', run: status?.last_billing_run },
    { key: 'promo_endings', label: 'Promo endings', run: status?.last_promo_run },
    { key: 'cancellations', label: 'Cancellations', run: status?.last_cancellation_run },
    { key: 'scheduled_changes', label: 'Scheduled changes', run: status?.last_scheduled_changes_run },
  ];
  const sortedRuns = taskRuns
    .filter((t) => t.run)
    .sort((a, b) => new Date(b.run.started_at) - new Date(a.run.started_at));
  const latestRun = sortedRuns[0]?.run || null;
  const latestRunLabel = !latestRun
    ? 'Never'
    : latestRun.status === 'completed'
    ? (latestRun.processed === 0 ? 'Nothing to process' : 'Completed')
    : latestRun.status === 'partial'
    ? 'Partial'
    : latestRun.status === 'failed'
    ? 'Failed'
    : latestRun.status === 'running'
    ? 'Running'
    : latestRun.status;

  const pendingBreakdown = [
    { count: status?.pending_billing_count || 0, one: 'billing charge', many: 'billing charges' },
    { count: status?.pending_promo_count || 0, one: 'promo ending', many: 'promo endings' },
    { count: status?.pending_cancellation_count || 0, one: 'cancellation', many: 'cancellations' },
    { count: status?.pending_scheduled_changes_count || 0, one: 'plan change', many: 'plan changes' },
  ];
  const pendingTotal = pendingBreakdown.reduce((sum, p) => sum + p.count, 0);
  const pendingSummary = pendingBreakdown
    .filter((p) => p.count > 0)
    .map((p) => `${p.count} ${p.count === 1 ? p.one : p.many}`)
    .join(' · ');

  const toolToggleClass = (on) => `${BTN} ${on ? 'border-ui-blue-line bg-ui-blue-bg text-ui-blue-fg hover:bg-ui-blue-bg' : ''}`;

  return (
    <div className="min-h-screen bg-ui-page px-4 pb-10 pt-7 text-ui-ink sm:px-8">
      <div className="flex max-w-[1120px] flex-col gap-5">
        {/* Header */}
        <header className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <div className="flex flex-wrap items-center gap-2.5">
              <h1 className="m-0 text-2xl font-bold tracking-tight text-ui-ink">Billing scheduler</h1>
              <span className={`${PILL} ${statusTone}`}>
                {status?.is_running && !config?.paused && config?.enabled
                  ? <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-current" aria-hidden="true" />
                  : DOT}
                {statusLabel}
              </span>
            </div>
            <p className="mt-1 text-sm text-ui-muted">
              Manage automatic subscription billing
              {status?.current_task && (
                <span className="ml-1 text-ui-ink-3">(processing: {status.current_task})</span>
              )}
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <button type="button" onClick={() => navigate('/admin/scheduler/history')} className={`${BTN} h-10`}>
              <History className="h-4 w-4" />
              History
            </button>
            <button
              type="button"
              onClick={() => setShowConfig(!showConfig)}
              aria-expanded={showConfig}
              className={`${toolToggleClass(showConfig)} h-10`}
            >
              <Settings className="h-4 w-4" />
              Configure
            </button>
            <button
              type="button"
              onClick={handlePauseResume}
              disabled={actionLoading || !config?.enabled}
              className={`${BTN} h-10`}
            >
              {config?.paused ? (
                <>
                  <Play className="h-4 w-4" />
                  Resume
                </>
              ) : (
                <>
                  <Pause className="h-4 w-4" />
                  Pause
                </>
              )}
            </button>
            <button
              type="button"
              onClick={() => handleTrigger('all', true)}
              disabled={actionLoading}
              className={`${BTN} h-10`}
            >
              <Eye className="h-4 w-4" />
              Dry run
            </button>
            <button
              type="button"
              onClick={() => handleTrigger('all', false)}
              disabled={actionLoading}
              className={BTN_P}
            >
              {actionLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Zap className="h-4 w-4" />}
              Run all tasks now
            </button>
          </div>
        </header>

        {/* Alerts */}
        {error && (
          <Notice tone="red" icon={<XCircle className="h-5 w-5 shrink-0" />} onDismiss={() => setError('')} dismissLabel="Dismiss error">
            {error}
          </Notice>
        )}

        {success && (
          <Notice tone="green" icon={<CheckCircle className="h-5 w-5 shrink-0" />} onDismiss={() => setSuccess('')} dismissLabel="Dismiss message">
            {success}
          </Notice>
        )}

        {config?.paused && config?.paused_by_name && (
          <Notice tone="amber" icon={<Pause className="h-4 w-4 shrink-0" />}>
            Paused by {config.paused_by_name} at {formatDate(config.paused_at)}
          </Notice>
        )}

        {config?.dry_run && (
          <Notice tone="blue" icon={<Eye className="h-4 w-4 shrink-0" />}>
            Dry run mode is enabled - actions will be logged but cards won't be charged
          </Notice>
        )}

        {/* Stat panels */}
        <div className="grid grid-cols-[repeat(auto-fit,minmax(min(200px,100%),1fr))] gap-3">
          <div className={`${PANEL} p-4`}>
            <div className={LBL}>Next run</div>
            <div className="mt-2 font-mono text-[22px] font-semibold text-ui-ink">
              {status?.next_scheduled_run ? formatDate(status.next_scheduled_run) : 'Not scheduled'}
            </div>
            <div className="mt-0.5 text-[13px] text-ui-muted">
              {config?.run_interval ? (RUN_INTERVAL_LABELS[config.run_interval] || config.run_interval) : 'No interval set'}
            </div>
          </div>
          <div className={`${PANEL} p-4`}>
            <div className={LBL}>Pending items</div>
            <div className="mt-2 text-[22px] font-semibold text-ui-ink">{pendingTotal}</div>
            <div className="mt-0.5 text-[13px] text-ui-muted">{pendingSummary || 'Nothing pending'}</div>
          </div>
          <div className={`${PANEL} p-4`}>
            <div className={LBL}>Last run</div>
            <div className="mt-2 flex flex-wrap items-center gap-2">
              <span className="text-[22px] font-semibold text-ui-ink">{latestRunLabel}</span>
              {latestRun?.failed > 0 && (
                <span className={`${PILL} ${latestRun.status === 'failed' ? TONE.red : TONE.amber}`}>
                  {latestRun.failed} failed
                </span>
              )}
            </div>
            <div className="mt-0.5 text-[13px] text-ui-muted">
              {latestRun ? (
                <>
                  {sortedRuns[0].label} · <span className="font-mono">{formatDate(latestRun.started_at)}</span>
                </>
              ) : 'No runs recorded yet'}
            </div>
          </div>
        </div>

        {/* Configuration + recent runs */}
        <div className="flex flex-wrap items-start gap-4">
          {showConfig && (
            <SchedulerConfigPanel
              config={config}
              onClose={() => setShowConfig(false)}
              onSave={async () => {
                await fetchStatus();
                setShowConfig(false);
              }}
            />
          )}

          <section className={`${PANEL} min-w-0 flex-[999_1_520px] overflow-hidden`} aria-labelledby="sched-runs-h">
            <div className="flex items-center justify-between gap-3 px-4 pb-3 pt-4">
              <h2 id="sched-runs-h" className="m-0 text-base font-bold text-ui-ink">Latest run per task</h2>
              <button
                type="button"
                onClick={() => navigate('/admin/scheduler/history')}
                className="text-[13px] text-ui-link hover:text-ui-blue-fg"
              >
                Full history
              </button>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full border-collapse text-sm">
                <thead>
                  <tr>
                    <th scope="col" className={TH}>Task</th>
                    <th scope="col" className={TH}>Started</th>
                    <th scope="col" className={TH}>Result</th>
                    <th scope="col" className={TH}>Processed</th>
                    <th scope="col" className={TH}>Succeeded</th>
                    <th scope="col" className={TH}>Failed</th>
                    <th scope="col" className={TH}>Duration</th>
                  </tr>
                </thead>
                <tbody>
                  {taskRuns.map(({ key, label, run }) => (
                    <tr key={key}>
                      <td className={`${TD} font-medium text-ui-ink`}>{label}</td>
                      <td className={`${TD} whitespace-nowrap font-mono`}>{run ? formatDate(run.started_at) : 'Never'}</td>
                      <td className={TD}><RunResultPill run={run} /></td>
                      <td className={TD}>{run ? run.processed : '—'}</td>
                      <td className={TD}>{run ? run.succeeded : '—'}</td>
                      <td className={`${TD} ${run?.failed > 0 ? 'font-semibold text-ui-red-fg' : ''}`}>{run ? run.failed : '—'}</td>
                      <td className={`${TD} font-mono`}>
                        {run?.duration_seconds != null ? `${run.duration_seconds.toFixed(1)}s` : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        </div>

        {/* Tools */}
        <section className={`${PANEL} p-4`} aria-labelledby="sched-tools-h">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <h2 id="sched-tools-h" className="m-0 text-base font-bold text-ui-ink">Tools</h2>
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => setShowDateSearch(!showDateSearch)}
                aria-expanded={showDateSearch}
                className={toolToggleClass(showDateSearch)}
              >
                <Calendar className="h-4 w-4" />
                Search date range
              </button>
              <button
                type="button"
                onClick={() => setShowSimulation(!showSimulation)}
                aria-expanded={showSimulation}
                className={toolToggleClass(showSimulation)}
              >
                <Clock className="h-4 w-4" />
                Simulate date
              </button>
              <button
                type="button"
                onClick={() => {
                  fetchStatus();
                  fetchPreview();
                }}
                disabled={actionLoading}
                className={BTN}
              >
                <RefreshCw className="h-4 w-4" />
                Refresh
              </button>
            </div>
          </div>
        </section>

        {/* Date Range Search Panel */}
        {showDateSearch && (
          <section className={`${PANEL} p-5`} aria-labelledby="sched-search-h">
            <div className="mb-4 flex items-center justify-between">
              <h2 id="sched-search-h" className="m-0 flex items-center gap-2 text-base font-bold text-ui-ink">
                <Calendar className="h-4 w-4 text-ui-violet-fg" />
                Search scheduled charges
              </h2>
              <button
                type="button"
                onClick={() => {
                  setShowDateSearch(false);
                  setScheduledCharges(null);
                }}
                aria-label="Close scheduled charges search"
                className="rounded-lg p-1.5 text-ui-faint hover:bg-ui-hover hover:text-ui-ink-2"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            <div className="mb-4 flex flex-wrap items-end gap-4">
              <div>
                <label htmlFor="sched-search-start" className={`mb-1 block ${LBL}`}>Start date</label>
                <input
                  id="sched-search-start"
                  type="date"
                  value={dateSearchStart}
                  onChange={(e) => setDateSearchStart(e.target.value)}
                  className={INPUT}
                />
              </div>
              <div>
                <label htmlFor="sched-search-end" className={`mb-1 block ${LBL}`}>End date</label>
                <input
                  id="sched-search-end"
                  type="date"
                  value={dateSearchEnd}
                  onChange={(e) => setDateSearchEnd(e.target.value)}
                  className={INPUT}
                />
              </div>
              <button type="button" onClick={handleDateSearch} disabled={dateSearchLoading} className={BTN_P}>
                {dateSearchLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Calendar className="h-4 w-4" />}
                Search
              </button>
            </div>

            {/* Search Results */}
            {scheduledCharges && (
              <div className="mt-4">
                <div className="mb-3 flex flex-wrap items-center justify-between gap-2 text-sm">
                  <span className="text-ui-muted">
                    {scheduledCharges.start_date} to {scheduledCharges.end_date}
                  </span>
                  <span className="font-medium text-ui-ink">
                    {scheduledCharges.total_subscriptions} charges totaling {formatAmount(scheduledCharges.total_amount)}
                  </span>
                </div>

                {scheduledCharges.by_date?.length > 0 ? (
                  <div className="max-h-96 space-y-3 overflow-y-auto">
                    {scheduledCharges.by_date.map((dayData) => (
                      <div key={dayData.date} className="overflow-hidden rounded-xl border border-ui-line">
                        <div className="flex items-center justify-between border-b border-ui-divider bg-ui-sunk px-4 py-2">
                          <span className="font-medium text-ui-ink">{dayData.date}</span>
                          <span className="text-sm text-ui-muted">
                            {dayData.count} charge{dayData.count !== 1 ? 's' : ''} · {formatAmount(dayData.total_amount)}
                          </span>
                        </div>
                        <div className="divide-y divide-ui-divider">
                          {dayData.subscriptions.map((sub) => (
                            <div key={sub.id} className="flex items-center justify-between gap-3 px-4 py-2 text-sm">
                              <div className="flex min-w-0 flex-wrap items-center gap-2">
                                <span className="font-medium text-ui-ink">{sub.organization_name}</span>
                                <span className="text-ui-muted">{sub.package_name}</span>
                                <span className={`${PILL} ${TONE.neutral} h-5 px-2 font-medium`}>
                                  {sub.payment_type === 'credit_card' ? (
                                    <><CreditCard className="h-3 w-3" aria-hidden="true" />Card</>
                                  ) : (
                                    <><FileText className="h-3 w-3" aria-hidden="true" />Direct Bill</>
                                  )}
                                </span>
                              </div>
                              <span className="font-medium text-ui-ink">{formatAmount(sub.effective_price)}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="py-8 text-center text-ui-muted">
                    No scheduled charges in this date range
                  </div>
                )}
              </div>
            )}
          </section>
        )}

        {/* Date Simulation Panel */}
        {showSimulation && (
          <section className={`${PANEL} p-5`} aria-labelledby="sched-sim-h">
            <div className="mb-4 flex items-center justify-between">
              <h2 id="sched-sim-h" className="m-0 flex items-center gap-2 text-base font-bold text-ui-ink">
                <Clock className="h-4 w-4 text-ui-orange-fg" />
                Simulate &amp; test billing for date
              </h2>
              <button
                type="button"
                onClick={() => {
                  setShowSimulation(false);
                  setSimulationPreview(null);
                  setTestExecutionResults(null);
                  setShowTestConfirm(false);
                }}
                aria-label="Close billing simulation"
                className="rounded-lg p-1.5 text-ui-faint hover:bg-ui-hover hover:text-ui-ink-2"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            {/* Stripe Mode Indicator */}
            {stripeSandboxMode && (
              <div className="mb-4">
                <Notice
                  tone={stripeSandboxMode.sandbox_mode ? 'amber' : 'red'}
                  icon={<AlertCircle className="h-4 w-4 shrink-0" />}
                >
                  {stripeSandboxMode.sandbox_mode
                    ? 'Stripe Sandbox Mode - Test execution is available'
                    : 'Stripe Production Mode - Test execution is NOT available'
                  }
                </Notice>
              </div>
            )}

            <p className="mb-4 text-sm text-ui-muted">
              Preview what would be processed, run a dry-run simulation, or execute actual test billing in sandbox mode.
            </p>

            <div className="mb-4 flex flex-wrap items-end gap-3">
              <div>
                <label htmlFor="sched-sim-date" className={`mb-1 block ${LBL}`}>Date</label>
                <input
                  id="sched-sim-date"
                  type="date"
                  value={simulationDate}
                  onChange={(e) => {
                    setSimulationDate(e.target.value);
                    setSimulationPreview(null);
                    setTestExecutionResults(null);
                    setShowTestConfirm(false);
                  }}
                  className={INPUT}
                />
              </div>
              <button
                type="button"
                onClick={handleGetPreviewForDate}
                disabled={simulationLoading || !simulationDate}
                className={BTN}
              >
                {simulationLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Eye className="h-4 w-4" />}
                Preview
              </button>
              <button
                type="button"
                onClick={handleRunSimulation}
                disabled={simulationLoading || !simulationDate}
                className={BTN}
              >
                {simulationLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Play className="h-4 w-4" />}
                Dry Run
              </button>
              {stripeSandboxMode?.sandbox_mode && (
                <>
                  {!showTestConfirm ? (
                    <button
                      type="button"
                      onClick={() => setShowTestConfirm(true)}
                      disabled={simulationLoading || !simulationDate}
                      className={BTN_P}
                      title="Execute actual billing in sandbox mode"
                    >
                      <Zap className="h-4 w-4" />
                      Execute Test
                    </button>
                  ) : (
                    <div className="flex flex-wrap items-center gap-2 rounded-lg border border-ui-red-line bg-ui-red-bg p-2">
                      <span className="text-sm text-ui-red-fg">
                        {simulationPreview?.billing?.count
                          ? `Charge ${simulationPreview.billing.count} subscription(s)?`
                          : 'Process billing for this date?'
                        }
                      </span>
                      <button
                        type="button"
                        onClick={handleExecuteTest}
                        disabled={simulationLoading}
                        className={BTN_DANGER}
                      >
                        Yes, Execute
                      </button>
                      <button
                        type="button"
                        onClick={() => setShowTestConfirm(false)}
                        className={`${BTN} h-8`}
                      >
                        Cancel
                      </button>
                    </div>
                  )}
                </>
              )}
            </div>

            {/* Test Execution Results */}
            {testExecutionResults && (
              <div className="mb-4 mt-4 overflow-hidden rounded-xl border border-ui-line">
                <div className="flex items-center justify-between gap-2 border-b border-ui-divider bg-ui-sunk px-4 py-3">
                  <h3 className="m-0 flex items-center gap-2 font-semibold text-ui-ink">
                    {testExecutionResults.status === 'completed' ? (
                      <CheckCircle className="h-5 w-5 text-ui-green-fg" />
                    ) : testExecutionResults.status === 'partial' ? (
                      <AlertCircle className="h-5 w-5 text-ui-amber-fg" />
                    ) : (
                      <XCircle className="h-5 w-5 text-ui-red-fg" />
                    )}
                    Test Execution Results
                  </h3>
                  <span className={`${PILL} ${
                    testExecutionResults.status === 'completed' ? TONE.green
                      : testExecutionResults.status === 'partial' ? TONE.amber : TONE.red
                  }`}>
                    {DOT}
                    {testExecutionResults.status === 'completed' ? 'Completed'
                      : testExecutionResults.status === 'partial' ? 'Partial' : 'Failed'}
                  </span>
                </div>
                <div className="p-4">
                  {/* Summary */}
                  <div className="mb-4 grid grid-cols-2 gap-3 md:grid-cols-4">
                    <div className="rounded-lg border border-ui-divider bg-ui-sunk p-3 text-center">
                      <div className="text-2xl font-bold text-ui-ink">{testExecutionResults.summary?.total_processed || 0}</div>
                      <div className="text-sm text-ui-muted">Processed</div>
                    </div>
                    <div className="rounded-lg border border-ui-divider bg-ui-sunk p-3 text-center">
                      <div className="text-2xl font-bold text-ui-green-fg">{testExecutionResults.summary?.succeeded || 0}</div>
                      <div className="text-sm text-ui-muted">Succeeded</div>
                    </div>
                    <div className="rounded-lg border border-ui-divider bg-ui-sunk p-3 text-center">
                      <div className="text-2xl font-bold text-ui-red-fg">{testExecutionResults.summary?.failed || 0}</div>
                      <div className="text-sm text-ui-muted">Failed</div>
                    </div>
                    <div className="rounded-lg border border-ui-divider bg-ui-sunk p-3 text-center">
                      <div className="text-2xl font-bold text-ui-ink">
                        {formatAmount(testExecutionResults.summary?.total_amount_charged || 0)}
                      </div>
                      <div className="text-sm text-ui-muted">Total Charged</div>
                    </div>
                  </div>

                  {/* Detailed Results */}
                  {testExecutionResults.results?.length > 0 && (
                    <div className="max-h-64 space-y-2 overflow-y-auto">
                      <h4 className="text-sm font-medium text-ui-ink-2">Detailed Results:</h4>
                      {testExecutionResults.results.map((result, idx) => (
                        <div key={idx} className={SUB_ROW}>
                          <div className="flex items-center gap-2">
                            {result.success ? (
                              <CheckCircle className="h-4 w-4 shrink-0 text-ui-green-fg" />
                            ) : (
                              <XCircle className="h-4 w-4 shrink-0 text-ui-red-fg" />
                            )}
                            <div>
                              <span className="font-medium text-ui-ink">{result.organization_name}</span>
                              <span className="ml-2 text-ui-muted">{result.package_name}</span>
                            </div>
                          </div>
                          <div className="text-right">
                            <div className="font-medium text-ui-ink">{formatAmount(result.amount)}</div>
                            {result.success && result.invoice_id && (
                              <div className="text-xs text-ui-muted">
                                Invoice: {result.invoice_id.slice(0, 8)}...
                              </div>
                            )}
                            {!result.success && result.error && (
                              <div className="text-xs text-ui-red-fg">{result.error}</div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Errors Summary */}
                  {testExecutionResults.errors?.length > 0 && (
                    <div className="mt-4 rounded-lg border border-ui-red-line bg-ui-red-bg p-3 text-ui-red-fg">
                      <h4 className="mb-2 text-sm font-medium">Errors:</h4>
                      <ul className="space-y-1 text-sm">
                        {testExecutionResults.errors.map((err, idx) => (
                          <li key={idx}>
                            <strong>{err.organization_name}:</strong> {err.error}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  <div className="mt-3 font-mono text-xs text-ui-muted">
                    Run ID: {testExecutionResults.run_id} | Duration: {testExecutionResults.duration_seconds?.toFixed(2)}s
                  </div>
                </div>
              </div>
            )}

            {/* Simulation Preview Results */}
            {simulationPreview && !testExecutionResults && (
              <div className="mt-4 space-y-4">
                <div className="rounded-lg border border-ui-divider bg-ui-sunk p-3 text-sm text-ui-ink-2">
                  <span className="font-medium text-ui-ink">Simulating as of: </span>
                  {new Date(simulationPreview.as_of_date).toLocaleDateString()}
                </div>

                {/* Billing Summary */}
                <div className="rounded-xl border border-ui-line p-4">
                  <h3 className="mb-3 flex flex-wrap items-center gap-2 font-medium text-ui-ink">
                    <DollarSign className="h-4 w-4 text-ui-green-fg" />
                    Billing Charges ({simulationPreview.billing?.count || 0})
                    {simulationPreview.billing?.total_amount > 0 && (
                      <span className="ml-2 text-sm font-normal text-ui-muted">
                        Total: {formatAmount(simulationPreview.billing.total_amount)}
                      </span>
                    )}
                  </h3>
                  {simulationPreview.billing?.items?.length > 0 ? (
                    <div className="max-h-48 space-y-2 overflow-y-auto">
                      {simulationPreview.billing.items.map((item) => (
                        <div key={item.id} className={SUB_ROW}>
                          <div>
                            <span className="font-medium text-ui-ink">{item.organization_name}</span>
                            <span className="ml-2 text-ui-muted">{item.package_name}</span>
                          </div>
                          <span className="font-medium text-ui-ink">{formatAmount(item.effective_price)}</span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-ui-muted">No billing charges would be processed</p>
                  )}
                </div>

                {/* Other summaries */}
                <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
                  <div className="rounded-xl border border-ui-line p-3">
                    <h4 className={`mb-2 ${LBL}`}>Promo Endings</h4>
                    <p className="text-2xl font-bold text-ui-ink">{simulationPreview.promo_endings?.count || 0}</p>
                  </div>
                  <div className="rounded-xl border border-ui-line p-3">
                    <h4 className={`mb-2 ${LBL}`}>Cancellations</h4>
                    <p className="text-2xl font-bold text-ui-ink">{simulationPreview.cancellations?.count || 0}</p>
                  </div>
                  <div className="rounded-xl border border-ui-line p-3">
                    <h4 className={`mb-2 ${LBL}`}>Plan Changes</h4>
                    <p className="text-2xl font-bold text-ui-ink">{simulationPreview.scheduled_changes?.count || 0}</p>
                  </div>
                </div>
              </div>
            )}
          </section>
        )}

        {/* Task Status Grid */}
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-4">
          <TaskCard
            title="Billing Charges"
            icon={<DollarSign className="h-5 w-5 text-ui-green-fg" />}
            lastRun={status?.last_billing_run}
            pendingCount={status?.pending_billing_count || 0}
            onTrigger={() => handleTrigger('billing', false)}
            actionLoading={actionLoading}
          />
          <TaskCard
            title="Promo Endings"
            icon={<Calendar className="h-5 w-5 text-ui-violet-fg" />}
            lastRun={status?.last_promo_run}
            pendingCount={status?.pending_promo_count || 0}
            onTrigger={() => handleTrigger('promo_endings', false)}
            actionLoading={actionLoading}
          />
          <TaskCard
            title="Cancellations"
            icon={<XCircle className="h-5 w-5 text-ui-red-fg" />}
            lastRun={status?.last_cancellation_run}
            pendingCount={status?.pending_cancellation_count || 0}
            onTrigger={() => handleTrigger('cancellations', false)}
            actionLoading={actionLoading}
          />
          <TaskCard
            title="Scheduled Changes"
            icon={<ArrowRight className="h-5 w-5 text-ui-blue-fg" />}
            lastRun={status?.last_scheduled_changes_run}
            pendingCount={status?.pending_scheduled_changes_count || 0}
            onTrigger={() => handleTrigger('scheduled_changes', false)}
            actionLoading={actionLoading}
          />
        </div>

        {/* Pending Items Preview */}
        {preview && (
          <section className={`${PANEL} p-5`} aria-labelledby="sched-pending-h">
            <h2 id="sched-pending-h" className="m-0 mb-4 text-base font-bold text-ui-ink">Pending items</h2>

            {/* Billing */}
            {preview.billing?.count > 0 && (
              <div className="mb-6">
                <div className="mb-3 flex items-center justify-between">
                  <h3 className="m-0 font-medium text-ui-ink">
                    Due for Billing ({preview.billing.count})
                  </h3>
                  <span className="text-sm text-ui-muted">
                    Total: {formatAmount(preview.billing.total_amount)}
                  </span>
                </div>
                <div className="space-y-2">
                  {preview.billing.items.slice(0, 5).map((item) => (
                    <div key={item.id} className={SUB_ROW}>
                      <div>
                        <span className="font-medium text-ui-ink">{item.organization_name}</span>
                        <span className="ml-2 text-sm text-ui-muted">{item.package_name}</span>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="font-medium text-ui-ink">{formatAmount(item.effective_price)}</span>
                        <button
                          type="button"
                          onClick={() => handleTriggerSubscription(item.id, false)}
                          disabled={actionLoading}
                          className={`${BTN} h-8`}
                        >
                          Charge
                        </button>
                      </div>
                    </div>
                  ))}
                  {preview.billing.count > 5 && (
                    <div className="py-2 text-center text-sm text-ui-muted">
                      ... and {preview.billing.count - 5} more
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Promo Endings */}
            {preview.promo_endings?.count > 0 && (
              <div className="mb-6">
                <h3 className="m-0 mb-3 font-medium text-ui-ink">
                  Promos Ending Today ({preview.promo_endings.count})
                </h3>
                <div className="space-y-2">
                  {preview.promo_endings.items.slice(0, 3).map((item) => (
                    <div key={item.id} className={SUB_ROW}>
                      <div>
                        <span className="font-medium text-ui-ink">{item.organization_name}</span>
                        <span className="ml-2 text-sm text-ui-muted">
                          {item.promo_type === 'free_trial' ? 'Trial ends' : 'Promo ends'}
                        </span>
                      </div>
                      <span className="text-sm text-ui-ink-3">
                        → {formatAmount(item.effective_price)}/
                        {item.billing_interval === 'yearly' ? 'year' : 'month'}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Cancellations */}
            {preview.cancellations?.count > 0 && (
              <div className="mb-6">
                <h3 className="m-0 mb-3 font-medium text-ui-ink">
                  Pending Cancellations ({preview.cancellations.count})
                </h3>
                <div className="space-y-2">
                  {preview.cancellations.items.slice(0, 3).map((item) => (
                    <div key={item.id} className={SUB_ROW}>
                      <span className="font-medium text-ui-ink">{item.organization_name}</span>
                      <span className={`${PILL} ${TONE.red}`}>Will be cancelled</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Scheduled Changes */}
            {preview.scheduled_changes?.count > 0 && (
              <div>
                <h3 className="m-0 mb-3 font-medium text-ui-ink">
                  Scheduled Plan Changes ({preview.scheduled_changes.count})
                </h3>
                <div className="space-y-2">
                  {preview.scheduled_changes.items.slice(0, 3).map((item) => (
                    <div key={item.id} className={SUB_ROW}>
                      <div>
                        <span className="font-medium text-ui-ink">{item.organization_name}</span>
                        <span className="ml-2 text-sm text-ui-muted">
                          → {item.scheduled_package_name}
                        </span>
                      </div>
                      <span className="text-sm text-ui-ink-3">
                        {formatAmount(item.scheduled_effective_price)}/
                        {item.scheduled_billing_interval === 'yearly' ? 'year' : 'month'}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Empty state */}
            {!preview.billing?.count &&
              !preview.promo_endings?.count &&
              !preview.cancellations?.count &&
              !preview.scheduled_changes?.count && (
                <div className="py-8 text-center text-ui-muted">
                  <CheckCircle className="mx-auto mb-3 h-12 w-12 text-ui-green-fg" />
                  <p>No pending items to process</p>
                </div>
              )}
          </section>
        )}
      </div>
    </div>
  );
};

/**
 * TaskCard - Individual task status card
 */
const TaskCard = ({ title, icon, lastRun, pendingCount, onTrigger, actionLoading }) => {
  const formatDate = (dateStr) => {
    if (!dateStr) return 'Never';
    const date = new Date(dateStr);
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed':
        return 'text-ui-green-fg';
      case 'partial':
        return 'text-ui-amber-fg';
      case 'failed':
        return 'text-ui-red-fg';
      default:
        return 'text-ui-ink-3';
    }
  };

  return (
    <div className={`${PANEL} flex flex-col p-4`}>
      <div className="mb-3 flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 text-ui-ink">
          {icon}
          <span className="font-medium">{title}</span>
        </div>
        {pendingCount > 0 && (
          <span className={`${PILL} ${TONE.blue} h-5 px-2`}>
            {pendingCount} pending
          </span>
        )}
      </div>

      <div className="space-y-2 text-sm">
        <div className="flex justify-between">
          <span className="text-ui-muted">Last Run:</span>
          <span className={`inline-flex items-center gap-1 ${lastRun ? getStatusColor(lastRun.status) : 'text-ui-faint'}`}>
            <span className="font-mono">{lastRun ? formatDate(lastRun.started_at) : 'Never'}</span>
            {lastRun?.status === 'completed' && <CheckCircle className="h-3.5 w-3.5" aria-label="Completed" />}
            {lastRun?.status === 'partial' && <AlertCircle className="h-3.5 w-3.5" aria-label="Partial" />}
            {lastRun?.status === 'failed' && <XCircle className="h-3.5 w-3.5" aria-label="Failed" />}
          </span>
        </div>
        {lastRun && (
          <div className="flex justify-between text-ui-muted">
            <span>Processed:</span>
            <span className="text-ui-ink-2">
              {lastRun.succeeded}/{lastRun.processed}
              {lastRun.failed > 0 && (
                <span className="ml-1 text-ui-red-fg">({lastRun.failed} failed)</span>
              )}
            </span>
          </div>
        )}
      </div>

      <button
        type="button"
        onClick={onTrigger}
        disabled={actionLoading || pendingCount === 0}
        className={`${BTN} mt-3 w-full justify-center`}
      >
        <Play className="h-3.5 w-3.5" />
        Run Now
      </button>
    </div>
  );
};

/**
 * Switch - role="switch" toggle used by the config panel rows.
 */
const Switch = ({ checked, onChange, label, id }) => (
  <button
    type="button"
    id={id}
    role="switch"
    aria-checked={checked}
    aria-label={label}
    onClick={() => onChange(!checked)}
    className={`relative h-6 w-10 flex-none rounded-full transition-colors ${checked ? 'bg-ui-primary' : 'bg-ui-control'}`}
  >
    <span
      className={`absolute top-[3px] h-[18px] w-[18px] rounded-full bg-ui-on-primary shadow-ui-card transition-all ${checked ? 'left-[19px]' : 'left-[3px]'}`}
      aria-hidden="true"
    />
  </button>
);

const CONFIG_ROW = 'flex items-center justify-between gap-3 border-b border-ui-divider py-3 text-sm text-ui-ink';

/**
 * SchedulerConfigPanel - Configuration modal/panel
 */
const SchedulerConfigPanel = ({ config, onClose, onSave }) => {
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [formData, setFormData] = useState({
    enabled: config?.enabled || false,
    billing_hour: config?.billing_hour || 2,
    billing_minute: config?.billing_minute || 0,
    run_interval: config?.run_interval || 'daily',
    dry_run: config?.dry_run || false,
    pause_on_high_failure: config?.pause_on_high_failure ?? true,
    failure_threshold_percent: config?.failure_threshold_percent || 50,
    tasks_enabled: config?.tasks_enabled || {
      billing: true,
      promo_endings: true,
      cancellations: true,
      scheduled_changes: true,
    },
  });

  const handleSave = async () => {
    setError('');
    setSaving(true);

    try {
      await axios.put(
        `${getApiBase()}/admin/scheduler/config`,
        formData,
        { headers: getAuthHeaders() }
      );
      onSave();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to save configuration');
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className={`${PANEL} flex-[1_1_320px] px-5 pb-4 pt-1.5`} aria-labelledby="sched-cfg-h">
      <div className="mb-1 mt-3.5 flex items-center justify-between">
        <h2 id="sched-cfg-h" className="m-0 text-base font-bold text-ui-ink">Scheduler configuration</h2>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close scheduler configuration"
          className="rounded-lg p-1.5 text-ui-faint hover:bg-ui-hover hover:text-ui-ink-2"
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      {error && (
        <div className="my-2">
          <Notice tone="red" icon={<XCircle className="h-4 w-4 shrink-0" />}>{error}</Notice>
        </div>
      )}

      {/* Enable/Disable */}
      <div className={CONFIG_ROW}>
        <span>Scheduler enabled</span>
        <Switch
          label="Scheduler enabled"
          checked={formData.enabled}
          onChange={(v) => setFormData({ ...formData, enabled: v })}
        />
      </div>

      {/* Run Time */}
      <div className={CONFIG_ROW}>
        <span id="sched-cfg-time">Run time (UTC)</span>
        <div className="flex items-center gap-1.5" role="group" aria-labelledby="sched-cfg-time">
          <label htmlFor="sched-cfg-hour" className="sr-only">Hour (UTC)</label>
          <input
            id="sched-cfg-hour"
            type="number"
            min="0"
            max="23"
            value={formData.billing_hour}
            onChange={(e) => setFormData({ ...formData, billing_hour: parseInt(e.target.value) })}
            className={`${INPUT} w-16 font-mono`}
          />
          <span className="text-ui-muted">:</span>
          <label htmlFor="sched-cfg-minute" className="sr-only">Minute (UTC)</label>
          <input
            id="sched-cfg-minute"
            type="number"
            min="0"
            max="59"
            value={formData.billing_minute}
            onChange={(e) => setFormData({ ...formData, billing_minute: parseInt(e.target.value) })}
            className={`${INPUT} w-16 font-mono`}
          />
        </div>
      </div>

      {/* Run Interval */}
      <div className={CONFIG_ROW}>
        <label htmlFor="sched-cfg-interval">Run interval</label>
        <select
          id="sched-cfg-interval"
          value={formData.run_interval}
          onChange={(e) => setFormData({ ...formData, run_interval: e.target.value })}
          className={INPUT}
        >
          <option value="daily">Daily (Production)</option>
          <option value="hourly">Hourly (Testing)</option>
          <option value="15min">Every 15 minutes (Development)</option>
        </select>
      </div>

      {/* Dry Run */}
      <div className={CONFIG_ROW}>
        <div>
          <div>Dry run mode</div>
          <p className="m-0 text-[13px] text-ui-muted">Log actions but don't charge cards</p>
        </div>
        <Switch
          label="Dry run mode"
          checked={formData.dry_run}
          onChange={(v) => setFormData({ ...formData, dry_run: v })}
        />
      </div>

      {/* Auto-pause on high failure */}
      <div className={CONFIG_ROW}>
        <div>
          <div>Pause on high failure rate</div>
          <p className="m-0 text-[13px] text-ui-muted">
            Auto-pause if &gt;{formData.failure_threshold_percent}% of charges fail
          </p>
        </div>
        <Switch
          label="Pause on high failure rate"
          checked={formData.pause_on_high_failure}
          onChange={(v) => setFormData({ ...formData, pause_on_high_failure: v })}
        />
      </div>

      {/* Task Toggles */}
      <div className={`mt-4 ${LBL}`}>Enabled tasks</div>
      {[
        { key: 'billing', label: 'Process Billing Charges' },
        { key: 'promo_endings', label: 'Process Promo Endings' },
        { key: 'cancellations', label: 'Process Cancellations' },
        { key: 'scheduled_changes', label: 'Apply Scheduled Changes' },
      ].map(({ key, label }, idx, arr) => (
        <div key={key} className={`${CONFIG_ROW} ${idx === arr.length - 1 ? 'border-b-0' : ''}`}>
          <span>{label}</span>
          <Switch
            label={label}
            checked={!!formData.tasks_enabled[key]}
            onChange={(v) =>
              setFormData({
                ...formData,
                tasks_enabled: { ...formData.tasks_enabled, [key]: v },
              })
            }
          />
        </div>
      ))}

      <div className="mt-4 flex justify-end gap-2">
        <button type="button" onClick={onClose} className={`${BTN} h-10`}>
          Cancel
        </button>
        <button type="button" onClick={handleSave} disabled={saving} className={BTN_P}>
          {saving && <Loader2 className="h-4 w-4 animate-spin" />}
          Save configuration
        </button>
      </div>
    </section>
  );
};

export default SchedulerDashboard;
